export const PUBLIC_API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8002/api";

// NEXT_PUBLIC_API_BASE_URL is baked into the client bundle, so the browser
// always uses it. On the server, prefer API_INTERNAL_URL (a plain, non-public
// env var) when set - e.g. a fast direct http://localhost:8002 call instead
// of round-tripping through the public tunnel domain for SSR fetches. Both
// resolve to the same backend; this is purely a server-side shortcut.
export const API_BASE_URL =
  typeof window === "undefined"
    ? (process.env.API_INTERNAL_URL ?? PUBLIC_API_BASE_URL)
    : PUBLIC_API_BASE_URL;

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, data: unknown) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : undefined;
    super(detail ?? `Request failed with status ${status}`);
    this.status = status;
    this.data = data;
  }
}

export async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function jsonHeaders(options: RequestInit): HeadersInit {
  if (options.body instanceof FormData) return { ...options.headers };
  return { "Content-Type": "application/json", ...options.headers };
}

/** For use in Client Components: always sends/receives auth cookies, and
 * silently refreshes the access token once on a 401 before giving up. */
export async function apiFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const request = () =>
    fetch(`${API_BASE_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: jsonHeaders(options),
    });

  let response = await request();

  if (response.status === 401 && !path.startsWith("/auth/")) {
    const refreshed = await fetch(`${API_BASE_URL}/auth/refresh/`, {
      method: "POST",
      credentials: "include",
    });
    if (refreshed.ok) {
      response = await request();
    }
  }

  const data = await parseBody(response);
  if (!response.ok) throw new ApiError(response.status, data);
  return data as T;
}
